import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/api/client";
import { useMe } from "@/api/hooks";

type Role = "auditor" | "operator" | "admin";

interface UserRow {
  username: string;
  role: Role;
  created_at_sec: number;
  session_count: number;
}

interface UsersList {
  items: UserRow[];
}

function fmtTime(unixSec: number) {
  if (!unixSec) return "—";
  return new Date(unixSec * 1000).toISOString().slice(0, 10);
}

function RoleBadge({ role }: { role: Role }) {
  const cls =
    role === "admin"
      ? "bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
      : role === "operator"
      ? "bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
      : "bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300";
  return (
    <span className={`rounded ${cls} px-2 py-0.5 text-xs font-semibold uppercase tracking-wide`}>
      {role}
    </span>
  );
}

export function UsersPage() {
  const me = useMe();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const q = useQuery<UsersList>({
    queryKey: ["users"],
    queryFn: () => api<UsersList>("/api/v1/admin/users"),
    staleTime: 10_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["users"] });

  const onMutationError = (e: unknown) => {
    if (e instanceof ApiError) {
      const body = (typeof e.body === "object" && e.body) ? (e.body as { error?: string }) : null;
      setError(body?.error ?? e.message);
    } else {
      setError(String(e));
    }
  };

  const create = useMutation({
    mutationFn: (input: { username: string; password: string; role: Role }) =>
      api("/api/v1/admin/users", { method: "POST", body: input }),
    onSuccess: (_d, v) => {
      setNotice(`Created user '${v.username}' (${v.role}).`);
      setError(null);
      invalidate();
    },
    onError: onMutationError,
  });

  const del = useMutation({
    mutationFn: (username: string) =>
      api(`/api/v1/admin/users/${encodeURIComponent(username)}`, { method: "DELETE" }),
    onSuccess: (_d, username) => {
      setNotice(`Deleted user '${username}'.`);
      setError(null);
      invalidate();
    },
    onError: onMutationError,
  });

  const setRole = useMutation({
    mutationFn: (v: { username: string; role: Role }) =>
      api(`/api/v1/admin/users/${encodeURIComponent(v.username)}/role`, {
        method: "POST",
        body: { role: v.role },
      }),
    onSuccess: (_d, v) => {
      setNotice(`'${v.username}' is now ${v.role}.`);
      setError(null);
      invalidate();
    },
    onError: onMutationError,
  });

  const setPwd = useMutation({
    mutationFn: (v: { username: string; password: string }) =>
      api(`/api/v1/admin/users/${encodeURIComponent(v.username)}/password`, {
        method: "POST",
        body: { password: v.password },
      }),
    onSuccess: (_d, v) => {
      setNotice(`Password reset for '${v.username}'. Their sessions were invalidated.`);
      setError(null);
      invalidate();
    },
    onError: onMutationError,
  });

  const reset2fa = useMutation({
    mutationFn: (username: string) =>
      api(`/api/v1/admin/users/${encodeURIComponent(username)}/2fa`, { method: "DELETE" }),
    onSuccess: (_d, username) => {
      setNotice(`Two-factor authentication reset for '${username}'. They can sign in with just password and re-enrol.`);
      setError(null);
      invalidate();
    },
    onError: onMutationError,
  });

  if (q.error) {
    if (q.error instanceof ApiError && q.error.status === 401) {
      return (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-sm">
          Sign in as an administrator to manage users.
        </div>
      );
    }
    if (q.error instanceof ApiError && q.error.status === 403) {
      return (
        <div className="rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 px-3 py-2 text-sm">
          Administrators only. Your role: {me.data?.role ?? "unknown"}.
        </div>
      );
    }
    return (
      <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
        Could not load users: {(q.error as Error).message}
      </div>
    );
  }

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-xl font-semibold">Users</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1 max-w-3xl">
          Administrator management. Mutating actions invalidate the target
          user's active sessions, so they'll be re-prompted to log in.
          You cannot delete or demote your own account, nor the last
          remaining admin.
        </p>
      </header>

      {error && (
        <div className="rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300 mb-3 flex items-start justify-between gap-3">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-xs text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-200">dismiss</button>
        </div>
      )}
      {notice && (
        <div className="rounded bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 px-3 py-2 text-sm text-green-700 dark:text-green-300 mb-3 flex items-start justify-between gap-3">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-xs text-green-700 dark:text-green-300 hover:text-green-900 dark:hover:text-green-200">dismiss</button>
        </div>
      )}

      <CreateUserForm
        disabled={create.isPending}
        onSubmit={(username, password, role) => create.mutate({ username, password, role })}
      />

      <div className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-700 text-left text-xs uppercase tracking-wide text-slate-600 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">Username</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Active sessions</th>
              <th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && !q.data && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500 dark:text-slate-400">Loading…</td></tr>
            )}
            {q.data?.items.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500 dark:text-slate-400">No users.</td></tr>
            )}
            {q.data?.items.map((u) => {
              const isSelf = u.username === me.data?.username;
              return (
                <tr key={u.username} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 align-top">
                  <td className="px-3 py-2 font-mono">
                    {u.username}
                    {isSelf && <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">(you)</span>}
                  </td>
                  <td className="px-3 py-2"><RoleBadge role={u.role} /></td>
                  <td className="px-3 py-2 font-mono text-xs">{fmtTime(u.created_at_sec)}</td>
                  <td className="px-3 py-2 tabular-nums">{u.session_count}</td>
                  <td className="px-3 py-2 space-x-1">
                    <RoleChanger
                      current={u.role}
                      disabled={setRole.isPending}
                      onChange={(role) => {
                        if (confirm(`Change ${u.username}'s role to ${role}? Their active sessions will be invalidated.`)) {
                          setRole.mutate({ username: u.username, role });
                        }
                      }}
                    />
                    <button
                      className="text-xs rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700"
                      onClick={() => {
                        const pwd = prompt(`New password for ${u.username}:`);
                        if (pwd) setPwd.mutate({ username: u.username, password: pwd });
                      }}
                    >reset password</button>
                    <button
                      className="text-xs rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 hover:bg-slate-100 dark:hover:bg-slate-700"
                      onClick={() => {
                        if (confirm(`Reset two-factor authentication for ${u.username}? They'll be able to sign in with just password until they re-enrol.`)) {
                          reset2fa.mutate(u.username);
                        }
                      }}
                    >reset 2fa</button>
                    <button
                      disabled={isSelf}
                      title={isSelf ? "You can't delete your own account" : ""}
                      className="text-xs rounded border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 px-2 py-0.5 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-30"
                      onClick={() => {
                        if (confirm(`Delete user ${u.username}? This invalidates all their sessions.`)) {
                          del.mutate(u.username);
                        }
                      }}
                    >delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// --- subcomponents -----------------------------------------------------------

function CreateUserForm({
  disabled, onSubmit,
}: {
  disabled: boolean;
  onSubmit: (username: string, password: string, role: Role) => void;
}) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState<Role>("auditor");
  return (
    <form
      className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-3 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm items-end"
      onSubmit={(e) => {
        e.preventDefault();
        if (!username || !password) return;
        onSubmit(username, password, role);
        setUsername("");
        setPassword("");
        setRole("auditor");
      }}
    >
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">New username</span>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          autoComplete="off"
          className="font-mono mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
        />
      </label>
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Password</span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          autoComplete="new-password"
          className="font-mono mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1"
        />
      </label>
      <label className="flex flex-col">
        <span className="text-xs text-slate-500 dark:text-slate-400">Role</span>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as Role)}
          className="mt-1 rounded border border-slate-300 dark:border-slate-600 px-2 py-1 bg-white dark:bg-slate-800"
        >
          <option value="auditor">auditor</option>
          <option value="operator">operator</option>
          <option value="admin">admin</option>
        </select>
      </label>
      <button
        type="submit"
        disabled={disabled}
        className="rounded bg-slate-900 dark:bg-purple-600 text-white py-1.5 font-medium hover:bg-slate-800 dark:hover:bg-purple-500 disabled:opacity-50"
      >
        {disabled ? "Creating…" : "Add user"}
      </button>
    </form>
  );
}

function RoleChanger({
  current, disabled, onChange,
}: {
  current: Role;
  disabled: boolean;
  onChange: (role: Role) => void;
}) {
  return (
    <select
      value={current}
      disabled={disabled}
      onChange={(e) => {
        const next = e.target.value as Role;
        if (next !== current) onChange(next);
      }}
      className="text-xs rounded border border-slate-300 dark:border-slate-600 px-2 py-0.5 bg-white dark:bg-slate-800"
    >
      <option value="auditor">auditor</option>
      <option value="operator">operator</option>
      <option value="admin">admin</option>
    </select>
  );
}
